import { useState, useCallback, useEffect, useMemo } from 'react';
import { Modal } from 'react-bootstrap-v5';
import moment from 'moment';
import FolhaPagamentoGlobalService, { ISalarioLiquidoGrupo, ISalarioLiquidoResumoCompetencia } from '../../services/folhaPagamentoGlobalService';

import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

interface Props {
    show: boolean;
    onHide: () => void;
    esteiraId: number;
    contaEmpresa: string;
}

type FiltroOrigem = 'TODOS' | 'Folha' | 'Razão';

// Origem: de onde veio o vínculo — "Folha" (a ChaveVinculo existe numa rubrica importada) ou
// "Razão" (tudo o mais que vem da própria plataforma/ECD, inclusive o líquido desta tela, que
// nunca depende de arquivo importado). Não confundir com o Tipo (crédito/débito) da linha.
const ORIGEM_COR: Record<string, string> = {
    'Folha': '#6f42c1',
    'Razão': '#5E6397',
};
const TIPO_LABEL: Record<string, string> = {
    'C': 'Provisão',
    'D': 'Pagamento',
};
const TIPO_COR: Record<string, string> = {
    'C': '#0d6efd',
    'D': '#28a745',
};

const formatVal = (v: number) => v.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });
const MES_LABEL = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// Competência estimada do grupo pela data das linhas de Provisão (crédito) — só exibição.
const competenciaDoGrupo = (grupo: ISalarioLiquidoGrupo): string => {
    const provisoes = grupo.lancamentos.filter(l => l.tipoLancamento === 'C');
    if (provisoes.length === 0) return '—';
    const data = moment.min(provisoes.map(l => moment(l.dataLancamento)));
    return data.format('MM/YYYY');
};

const ModalSalarioLiquidoAnalise = ({ show, onHide, esteiraId, contaEmpresa }: Props) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [grupos, setGrupos] = useState<ISalarioLiquidoGrupo[]>([]);
    const [resumo, setResumo] = useState<ISalarioLiquidoResumoCompetencia[]>([]);
    const [filtroOrigem, setFiltroOrigem] = useState<FiltroOrigem>('TODOS');
    const [grupoExpandido, setGrupoExpandido] = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!esteiraId || !contaEmpresa) return;
        setLoading(true);
        try {
            const [gruposResult, resumoResult] = await Promise.all([
                FolhaPagamentoGlobalService.listarGruposLiquido(esteiraId, contaEmpresa),
                FolhaPagamentoGlobalService.listarResumoLiquido(esteiraId, contaEmpresa),
            ]);
            setGrupos(gruposResult);
            setResumo(resumoResult);
            setGrupoExpandido(gruposResult.length > 0 ? gruposResult[0].chaveVinculo : null);
        } finally {
            setLoading(false);
        }
    }, [esteiraId, contaEmpresa]);

    const pendentes = useMemo(() => resumo.filter(r => !r.vinculado), [resumo]);

    useEffect(() => {
        if (show) load();
    }, [show, load]);

    const totalProvisoes = useMemo(
        () => grupos.reduce((soma, g) => soma + g.lancamentos.filter(l => l.tipoLancamento === 'C').reduce((s, l) => s + l.valor, 0), 0),
        [grupos]
    );
    const totalPagamentos = useMemo(
        () => grupos.reduce((soma, g) => soma + g.lancamentos.filter(l => l.tipoLancamento === 'D').reduce((s, l) => s + l.valor, 0), 0),
        [grupos]
    );

    return (
        <Modal fullscreen scrollable show={show} onHide={onHide}>
            <div className="modal-content" style={{ backgroundColor: '#F4F5F7' }}>
                <div className="modal-header border-0 pb-1">
                    <div>
                        <h5 className="modal-title fw-bold mb-0" style={{ textTransform: 'uppercase', color: '#5E6397' }}>
                            {t('Análise de Conciliação — Líquido a Receber')}
                        </h5>
                        <small className="text-muted">{t('Soma do líquido apurado da competência x soma dos pagamentos achados na janela esperada (DEVN3-531)')}</small>
                    </div>
                    <button className="btn btn-icon btn-sm btn-active-light-primary ms-2" onClick={onHide}>
                        <i className="fas fa-times" style={{ color: '#5E6397' }} />
                    </button>
                </div>

                {loading && (
                    <div className="d-flex justify-content-center align-items-center py-5">
                        <span className="spinner-border spinner-border-sm me-2 text-primary" />
                        <span className="text-muted">{t('Carregando...')}</span>
                    </div>
                )}

                {!loading && resumo.length === 0 && (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                        <i className="fas fa-circle-exclamation fa-3x mb-3" style={{ color: '#ccc' }} />
                        <p className="mb-0">{t('Nenhuma competência de líquido encontrada nesta conta.')}</p>
                    </div>
                )}

                {!loading && resumo.length > 0 && (
                    <>
                        <div className="px-4 pt-3" style={{ backgroundColor: '#fff' }}>
                            <div className="row g-3 pb-3">
                                <div className="col-md-3">
                                    <div className="border rounded p-3 h-100 text-center">
                                        <div className="fw-bold" style={{ fontSize: '1.3rem', color: '#5E6397' }}>{grupos.length}</div>
                                        <small className="text-muted text-uppercase">{t('Competências conciliadas')}</small>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="border rounded p-3 h-100 text-center">
                                        <div className="fw-bold" style={{ fontSize: '1.3rem', color: TIPO_COR['C'] }}>{formatVal(totalProvisoes)}</div>
                                        <small className="text-muted text-uppercase">{t('Total Provisão (crédito)')}</small>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="border rounded p-3 h-100 text-center">
                                        <div className="fw-bold" style={{ fontSize: '1.3rem', color: TIPO_COR['D'] }}>{formatVal(totalPagamentos)}</div>
                                        <small className="text-muted text-uppercase">{t('Total Pagamento (débito)')}</small>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label text-muted text-uppercase mb-1" style={{ fontSize: '0.72rem' }}>{t('Filtrar por origem')}</label>
                                    <select
                                        className="form-select form-select-sm"
                                        value={filtroOrigem}
                                        onChange={e => setFiltroOrigem(e.target.value as FiltroOrigem)}
                                    >
                                        <option value="TODOS">{t('Todos')}</option>
                                        <option value="Folha">{t('Folha (importada)')}</option>
                                        <option value="Razão">{t('Razão (plataforma)')}</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="modal-body p-3">
                            <div className="d-flex flex-column gap-3">
                                {grupos.map(grupo => {
                                    const expandido = grupoExpandido === grupo.chaveVinculo;
                                    const linhas = grupo.lancamentos.filter(l => filtroOrigem === 'TODOS' || l.origem === filtroOrigem);
                                    const qtdProvisoes = grupo.lancamentos.filter(l => l.tipoLancamento === 'C').length;
                                    const qtdPagamentos = grupo.lancamentos.filter(l => l.tipoLancamento === 'D').length;
                                    const valorGrupo = grupo.lancamentos.filter(l => l.tipoLancamento === 'C').reduce((s, l) => s + l.valor, 0);

                                    return (
                                        <div key={grupo.chaveVinculo} className="border rounded bg-white">
                                            <div
                                                className="d-flex justify-content-between align-items-center p-3"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => setGrupoExpandido(expandido ? null : grupo.chaveVinculo)}
                                            >
                                                <div className="d-flex align-items-center gap-2">
                                                    <i className={`fas ${expandido ? 'fa-chevron-down' : 'fa-chevron-right'}`} style={{ fontSize: '0.7rem', color: '#5E6397' }} />
                                                    <span className="fw-bold" style={{ color: '#5E6397' }}>{t('Competência')} {competenciaDoGrupo(grupo)}</span>
                                                    <span className="badge bg-light text-dark border">{qtdProvisoes} {t('provisões')}</span>
                                                    <span className="badge bg-light text-dark border">{qtdPagamentos} {t('pagamentos')}</span>
                                                </div>
                                                <span className="fw-semibold" style={{ color: '#28a745' }}>{formatVal(valorGrupo)}</span>
                                            </div>

                                            {expandido && (
                                                <div className="table-responsive border-top">
                                                    <table className="table table-sm table-hover align-middle mb-0">
                                                        <thead>
                                                            <tr style={{ backgroundColor: '#f8f9fa', fontSize: '0.72rem', textTransform: 'uppercase', color: '#6c757d' }}>
                                                                <th className="ps-3">{t('Tipo')}</th>
                                                                <th>{t('Origem')}</th>
                                                                <th>{t('Data')}</th>
                                                                <th>{t('Histórico')}</th>
                                                                <th className="text-end pe-3">{t('Valor')}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {linhas.map(l => (
                                                                <tr key={l.id} style={{ fontSize: '0.8rem' }}>
                                                                    <td className="ps-3">
                                                                        <span className="badge" style={{ backgroundColor: TIPO_COR[l.tipoLancamento] ?? '#999', color: '#fff' }}>
                                                                            {t(TIPO_LABEL[l.tipoLancamento] ?? l.tipoLancamento)}
                                                                        </span>
                                                                    </td>
                                                                    <td>
                                                                        <span className="badge" style={{ backgroundColor: ORIGEM_COR[l.origem] ?? '#999', color: '#fff' }} title={t('De onde veio o vínculo: importação de folha ou razão da plataforma')}>
                                                                            {t(l.origem)}
                                                                        </span>
                                                                    </td>
                                                                    <td>{moment(l.dataLancamento).format('DD/MM/YYYY')}</td>
                                                                    <td>{l.historico}</td>
                                                                    <td className="text-end pe-3">{formatVal(l.valor)}</td>
                                                                </tr>
                                                            ))}
                                                            {linhas.length === 0 && (
                                                                <tr>
                                                                    <td colSpan={5} className="text-center text-muted py-3">{t('Nenhum lançamento com esse filtro neste grupo.')}</td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {grupos.length === 0 && (
                                    <div className="text-center text-muted py-4">
                                        <i className="fas fa-hourglass-half fa-2x mb-2" style={{ color: '#ccc' }} />
                                        <p className="mb-0">{t('Nenhuma competência fechou ainda — veja as diferenças abaixo.')}</p>
                                    </div>
                                )}
                            </div>

                            {pendentes.length > 0 && (
                                <div className="mt-4">
                                    <small className="text-muted text-uppercase fw-bold d-block mb-2">
                                        <i className="fas fa-triangle-exclamation me-1" style={{ color: '#f0ad4e' }} />
                                        {t('Competências pendentes (soma não bateu)')}
                                    </small>
                                    <div className="table-responsive border rounded bg-white">
                                        <table className="table table-sm table-hover align-middle mb-0">
                                            <thead>
                                                <tr style={{ backgroundColor: '#f8f9fa', fontSize: '0.72rem', textTransform: 'uppercase', color: '#6c757d' }}>
                                                    <th className="ps-3">{t('Competência')}</th>
                                                    <th className="text-end">{t('Líquido apurado')}</th>
                                                    <th className="text-end">{t('Pago na janela')}</th>
                                                    <th className="text-end pe-3">{t('Diferença')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {pendentes.map(r => (
                                                    <tr key={`${r.ano}-${r.mes}`} style={{ fontSize: '0.82rem' }}>
                                                        <td className="ps-3 fw-semibold">{MES_LABEL[r.mes]}/{r.ano}</td>
                                                        <td className="text-end">{formatVal(r.somaLiquido)}</td>
                                                        <td className="text-end">{formatVal(r.somaPagamentos)}</td>
                                                        <td className="text-end pe-3 fw-semibold" style={{ color: '#dc3545' }}>{formatVal(r.diferenca)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

export default ModalSalarioLiquidoAnalise;
